/* EventsCompliancePanel — Micro CFO Module 2 / Session A.
 *
 * Renders the 7-family compliance events timeline derived from
 * filing_index.filings via GET /api/clients/:id/events. Read-only view
 * (no workflow buttons), CCFS-grade UI consistency.
 *
 * Mounted by /app/frontend/src/pages/admin/clients/MicroCFOWorkspace.jsx.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  History, ChevronDown, ChevronRight, RefreshCcw, AlertCircle,
  Users, ScrollText, Building2, Banknote, FileSpreadsheet, ShieldCheck, Layers,
} from 'lucide-react';
import { CollapsibleSection } from '@/pages/admin/ccfs/components/CollapsibleSection';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Per-family visual tokens. Kept in this file so it stays self-contained.
const FAMILY_META = {
  shareholding:   { icon: Layers,          color: 'text-indigo-600', bg: 'bg-indigo-50',  border: 'border-indigo-200' },
  auditor:        { icon: ShieldCheck,     color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  directors:      { icon: Users,           color: 'text-violet-600', bg: 'bg-violet-50',  border: 'border-violet-200' },
  reg_office:     { icon: Building2,       color: 'text-sky-600',    bg: 'bg-sky-50',     border: 'border-sky-200' },
  charges:        { icon: Banknote,        color: 'text-amber-600',  bg: 'bg-amber-50',   border: 'border-amber-200' },
  annual_filings: { icon: FileSpreadsheet, color: 'text-rose-600',   bg: 'bg-rose-50',    border: 'border-rose-200' },
  other:          { icon: ScrollText,      color: 'text-slate-500',  bg: 'bg-slate-50',   border: 'border-slate-200' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function FamilyCard({ family, openKey, setOpenKey }) {
  const meta = FAMILY_META[family.key] || FAMILY_META.other;
  const Icon = meta.icon;
  const open = openKey === family.key;
  const isEmpty = family.count === 0;
  return (
    <div
      className={`bg-white rounded-md border ${meta.border}`}
      data-testid={`event-family-${family.key}`}
    >
      <button
        type="button"
        onClick={() => !isEmpty && setOpenKey(open ? null : family.key)}
        disabled={isEmpty}
        className={`w-full flex items-center justify-between px-3 py-2.5 text-left ${isEmpty ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-50/60'}`}
        data-testid={`event-family-toggle-${family.key}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-7 h-7 rounded-full flex items-center justify-center ${meta.bg}`}>
            <Icon className={`w-4 h-4 ${meta.color}`} />
          </span>
          <span className="text-sm font-semibold text-slate-800">{family.label}</span>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isEmpty ? 'bg-slate-100 text-slate-400' : `${meta.bg} ${meta.color} border ${meta.border}`}`}>
            {family.count}
          </span>
        </div>
        {!isEmpty && (
          open
            ? <ChevronDown className="w-4 h-4 text-slate-400" />
            : <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>
      {open && !isEmpty && (
        <div className="border-t border-slate-100 divide-y divide-slate-100 max-h-[360px] overflow-y-auto" data-testid={`event-family-list-${family.key}`}>
          {family.events.map((e, i) => (
            <div
              key={`${family.key}-${i}`}
              className="px-3 py-2 hover:bg-slate-50/60 flex items-start gap-3"
              data-testid={`event-row-${family.key}-${i}`}
            >
              <div className="shrink-0 w-20 text-[11px] font-mono text-slate-500 pt-0.5">
                {fmtDate(e.date_of_filing)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${meta.bg} ${meta.color}`}>
                    {e.form_name}
                  </span>
                  {e.file_type && (
                    <span className="text-[10px] uppercase font-mono px-1 py-0.5 rounded bg-slate-100 text-slate-500">
                      {e.file_type}
                    </span>
                  )}
                </div>
                {e.description && (
                  <p className="text-xs text-slate-600 mt-1 leading-snug">{e.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EventsCompliancePanel({ client, activeSection, setActiveSection }) {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const sectionKey = `events-${client.id}`;
  const hasFilingIndex = !!(client.filing_index && (client.filing_index.total_count || (client.filing_index.filings || []).length));

  const fetchEvents = useCallback(async () => {
    if (!hasFilingIndex) {
      setView({ families: [], summary: { total: 0 } });
      return;
    }
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api/clients/${client.id}/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load events');
      setView(data);
    } catch (e) {
      toast.error(e.message || 'Failed to load events');
      setView({ families: [], summary: { total: 0 } });
    } finally {
      setLoading(false);
    }
  }, [client.id, hasFilingIndex]);

  // Re-fetch when this section is opened or when filing-index upload timestamp changes
  useEffect(() => {
    if (activeSection === sectionKey && !view) {
      fetchEvents();
    }
  }, [activeSection, sectionKey, view, fetchEvents]);

  const summary = view?.summary || { total: 0 };
  // Local count from filing_index so the badge is accurate before the section
  // is opened (the API view only loads lazily on expand).
  const localTotal = (client.filing_index?.total_count
                      || (client.filing_index?.filings || []).length) || 0;
  const displayedTotal = view ? (summary.total || 0) : localTotal;

  return (
    <CollapsibleSection
      icon={History}
      title="Events & Compliance History"
      borderColor="border-slate-200"
      iconColor="text-slate-500"
      bgHover="hover:bg-slate-50/50"
      chevronBg="bg-slate-200"
      sectionKey={sectionKey}
      testId={sectionKey}
      activeSection={activeSection}
      setActiveSection={setActiveSection}
      badge={hasFilingIndex ? (
        <>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
            {displayedTotal} events
          </span>
          {summary.uncategorized > 0 && (
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
              {summary.uncategorized} uncategorised
            </span>
          )}
        </>
      ) : null}
      headerRight={hasFilingIndex && view && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-[10px] gap-1"
          onClick={(e) => { e.stopPropagation(); fetchEvents(); }}
          disabled={loading}
          data-testid="events-refresh-btn"
        >
          <RefreshCcw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      )}
    >
      {!hasFilingIndex ? (
        <div className="p-4 flex items-start gap-2 text-xs text-slate-500">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
          <span>
            Upload the MCA Filing Index in the section above to auto-derive the
            events timeline. Each filed form is classified into one of 7 families
            (Shareholding, Auditor, Directors, Registered Office, Charges,
            Annual Filings, Other).
          </span>
        </div>
      ) : loading && !view ? (
        <div className="p-4 text-xs text-slate-400">Loading events…</div>
      ) : (
        <div className="p-3 space-y-2" data-testid="events-families-grid">
          {(view?.families || []).map((fam) => (
            <FamilyCard
              key={fam.key}
              family={fam}
              openKey={openKey}
              setOpenKey={setOpenKey}
            />
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}
